import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchStockMovements,
  fetchStockLevels,
  fetchStockAlerts,
  addStock,
  adjustStock,
} from '../redux/slices/stockSlice';
import { fetchParts } from '../redux/slices/partsSlice';
import {
  Modal,
  LoadingSpinner,
  StatsCard,
  EmptyState,
} from '../components/Common';
import { useSearch } from '../context/SearchContext';
import { useEntityDetails } from '../context/EntityDetailsContext';
import { useLanguage } from '../context/LanguageContext';
import { printTable } from '../utils/printTable';

const StockMovements = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { openPart } = useEntityDetails();
  const { t } = useLanguage();

  // Redux state
  const { movements, levels, alerts, loading } = useSelector(
    (state) => state.stock,
  );
  const { items: parts } = useSelector((state) => state.parts);

  // Local state
  const [activeTab, setActiveTab] = useState('movements');
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showAdjustStockModal, setShowAdjustStockModal] = useState(false);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [addStockData, setAddStockData] = useState({
    part_id: '',
    quantity: '',
    notes: '',
  });

  const [adjustStockData, setAdjustStockData] = useState({
    part_id: '',
    quantity: '',
    notes: '',
  });

  useEffect(() => {
    dispatch(fetchStockMovements());
    dispatch(fetchStockLevels());
    dispatch(fetchStockAlerts());
    dispatch(fetchParts());
  }, [dispatch]);

  // Unique suppliers from movements
  const supplierOptions = [
    ...new Set((movements || []).map((m) => m.supplier).filter(Boolean)),
  ];

  // Unique suppliers from alerts
  const alertSupplierOptions = [
    ...new Set((alerts || []).map((a) => a.supplier).filter(Boolean)),
  ];

  // Get stock status key -- levels rows carry available_quantity, alerts
  // rows are plain parts rows with quantity. Must be defined before the
  // filters below use it.
  const getStockStatusKey = (item) => {
    const qty = Number(item.available_quantity ?? item.quantity ?? 0);
    const minLevel = Number(item.min_stock_level ?? 5);
    if (qty <= 0) return 'out_of_stock';
    if (qty <= minLevel) return 'low_stock';
    return 'in_stock';
  };

  const stockStatusLabel = (status) =>
    ({
      in_stock: t('stockMovements.inStock'),
      low_stock: t('stockMovements.lowStock'),
      out_of_stock: t('stockMovements.outOfStock'),
    })[status];

  // Case-insensitive "does any of these fields contain the search term"
  const term = searchTerm.trim().toLowerCase();
  const matchesTerm = (...fields) =>
    !term ||
    fields.some((f) =>
      f !== null && f !== undefined && String(f).toLowerCase().includes(term),
    );

  // Switching tabs resets the tab-specific filters, so a supplier picked on
  // Movements doesn't silently empty the Alerts list (and vice versa).
  const switchTab = (tab) => {
    setActiveTab(tab);
    setFilterSupplier('');
    setFilterStatus('');
  };

  // Filter movements
  const filteredMovements = (movements || []).filter((movement) => {
    const matchesSearch = matchesTerm(
      movement.part_name,
      movement.part_color,
      movement.sku,
      movement.supplier,
      movement.notes,
      movement.user_name,
      movement.movement_type,
      movement.reference_type,
      movement.reference_id,
    );

    const matchesSupplier =
      !filterSupplier || movement.supplier === filterSupplier;

    return matchesSearch && matchesSupplier;
  });

  // Filter levels (parts with stock info)
  const filteredLevels = (levels || []).filter((level) => {
    const matchesSearch = matchesTerm(
      level.part_name,
      level.color_name,
      level.sku,
      level.supplier,
      level.category,
      level.open_order_number,
    );

    const matchesStatus =
      !filterStatus || getStockStatusKey(level) === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Filter alerts
  const filteredAlerts = (alerts || []).filter((alert) => {
    const matchesSearch = matchesTerm(
      alert.name,
      alert.color,
      alert.sku,
      alert.supplier,
      alert.open_order_number,
    );
    const matchesSupplier =
      !filterSupplier || alert.supplier === filterSupplier;
    return matchesSearch && matchesSupplier;
  });

  const getDaysUntilEmpty = (partId) => {
    const latest = (movements || [])
      .filter((m) => m.part_id === partId)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    return latest?.days_until_empty ?? null;
  };

  // Handlers
  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await dispatch(
        addStock({
          part_id: parseInt(addStockData.part_id),
          quantity: parseInt(addStockData.quantity),
          notes: addStockData.notes,
        }),
      ).unwrap();

      setShowAddStockModal(false);
      setAddStockData({ part_id: '', quantity: '', notes: '' });
      refreshData();
    } catch (err) {
      alert(t('stockMovements.failedToAddStock', { error: err.message || err }));
    }
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    try {
      await dispatch(
        adjustStock({
          part_id: parseInt(adjustStockData.part_id),
          quantity: parseInt(adjustStockData.quantity),
          notes: adjustStockData.notes,
        }),
      ).unwrap();

      setShowAdjustStockModal(false);
      setAdjustStockData({ part_id: '', quantity: '', notes: '' });
      refreshData();
    } catch (err) {
      alert(t('stockMovements.failedToAdjustStock', { error: err.message || err }));
    }
  };

  const refreshData = () => {
    dispatch(fetchStockMovements());
    dispatch(fetchStockLevels());
    dispatch(fetchStockAlerts());
    dispatch(fetchParts());
  };

  // Helpers
  const getMovementTypeColor = (type) => {
    const colors = {
      in: 'bg-green-100 text-green-800',
      out: 'bg-red-100 text-red-800',
      adjustment: 'bg-blue-100 text-blue-800',
      production: 'bg-purple-100 text-purple-800',
      order_delivery: 'bg-teal-100 text-teal-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStockStatusColor = (status) => {
    const colors = {
      out_of_stock: 'bg-red-100 text-red-800',
      low_stock: 'bg-yellow-100 text-yellow-800',
      in_stock: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOpenOrderStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      ordered: 'bg-blue-100 text-blue-800',
      partial: 'bg-orange-100 text-orange-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Shared cell for the "already on order?" column on both the Stock
  // Levels and Low Stock Alerts tabs, so staff can see a part has an open
  // order (or is backordered) before placing a duplicate one.
  const renderOpenOrderCell = (item) => {
    if (!item.open_order_number) {
      return <span className="text-xs text-gray-400">{t('stockMovements.notOrdered')}</span>;
    }
    return (
      <div className="space-y-0.5">
        <span
          className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getOpenOrderStatusColor(item.open_order_status)}`}
        >
          {t(`stockMovements.orderStatus.${item.open_order_status}`) || item.open_order_status}
        </span>
        <p className="text-xs text-gray-400 font-mono">{item.open_order_number}</p>
        {item.open_order_backorder > 0 && (
          <p className="text-xs text-red-600 font-medium">
            {t('stockMovements.backorderQty', { qty: item.open_order_backorder })}
          </p>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Prints the active tab's filtered rows as a plain table
  const handlePrint = () => {
    const signedQty = (m) =>
      `${['in', 'order_delivery'].includes(m.movement_type) ? '+' : '-'}${m.quantity}`;
    const openOrder = (item) =>
      item.open_order_number
        ? `${item.open_order_number}${item.open_order_backorder > 0 ? ` (${t('stockMovements.backorderQty', { qty: item.open_order_backorder })})` : ''}`
        : '';
    const partLabel = (name, color) => (color ? `${name} (${color})` : name);

    if (activeTab === 'movements') {
      printTable({
        title: `${t('stockMovements.title')} – ${t('stockMovements.movementsTab', { count: filteredMovements.length })}`,
        subtitle: filterSupplier,
        columns: [
          { header: t('stockMovements.part') },
          { header: t('stockMovements.type') },
          { header: t('stockMovements.qty'), align: 'right' },
          { header: t('stockMovements.currentStock'), align: 'right' },
          { header: t('stockMovements.reference') },
          { header: t('stockMovements.supplierHeader') },
          { header: t('stockMovements.user') },
          { header: t('common.date') },
          { header: t('common.notes') },
        ],
        rows: filteredMovements.map((m) => [
          partLabel(m.part_name, m.part_color),
          m.movement_type?.toUpperCase(),
          signedQty(m),
          m.current_stock ?? '',
          m.reference_type ? `${m.reference_type}${m.reference_id ? ` #${m.reference_id}` : ''}` : '',
          m.supplier,
          m.user_name,
          formatDate(m.created_at),
          m.notes,
        ]),
      });
    } else if (activeTab === 'levels') {
      printTable({
        title: `${t('stockMovements.title')} – ${t('stockMovements.stockLevelsTab', { count: filteredLevels.length })}`,
        subtitle: filterStatus ? stockStatusLabel(filterStatus) : '',
        columns: [
          { header: t('stockMovements.part') },
          { header: t('stockMovements.orderNumber') },
          { header: t('stockMovements.quantity'), align: 'right' },
          { header: t('stockMovements.minLevel'), align: 'right' },
          { header: t('common.status') },
        ],
        rows: filteredLevels.map((l) => [
          partLabel(l.part_name, l.color_name),
          openOrder(l),
          l.total_quantity || 0,
          l.min_stock_level ?? 5,
          stockStatusLabel(getStockStatusKey(l)),
        ]),
      });
    } else {
      printTable({
        title: `${t('stockMovements.title')} – ${t('stockMovements.lowStockAlertsTab')}`,
        subtitle: filterSupplier,
        columns: [
          { header: t('stockMovements.part') },
          { header: t('stockMovements.current'), align: 'right' },
          { header: t('stockMovements.minLevel'), align: 'right' },
          { header: t('common.status') },
          { header: t('stockMovements.orderNr') },
          { header: t('stockMovements.daysUntilEmptyHeader'), align: 'right' },
          { header: t('stockMovements.supplierHeader') },
        ],
        rows: filteredAlerts.map((a) => [
          partLabel(a.name, a.color),
          a.quantity || 0,
          a.min_stock_level ?? 5,
          stockStatusLabel(getStockStatusKey(a)),
          openOrder(a),
          getDaysUntilEmpty(a.id) ?? '',
          a.supplier,
        ]),
      });
    }
  };

  // Stats
  const totalIn = (movements || [])
    .filter(
      (m) => m.movement_type === 'in' || m.movement_type === 'order_delivery',
    )
    .reduce((sum, m) => sum + (m.quantity || 0), 0);

  const totalOut = (movements || [])
    .filter(
      (m) => m.movement_type === 'out' || m.movement_type === 'production',
    )
    .reduce((sum, m) => sum + (m.quantity || 0), 0);

  const lowStockCount = (levels || []).filter((l) => {
    const status = getStockStatusKey(l);
    return status === 'low_stock';
  }).length;

  const outOfStockCount = (levels || []).filter((l) => {
    const status = getStockStatusKey(l);
    return status === 'out_of_stock';
  }).length;

  const partOptions = parts.map((p) => ({
    value: p.id,
    label: `${p.name} ${p.color ? `(${p.color})` : ''} - Current: ${p.quantity || 0}`,
    originalPart: p,
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('stockMovements.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('stockMovements.subtitle')}
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={handlePrint} className="btn-secondary">
            🖨️ {t('common.print')}
          </button>
          <button
            onClick={() => setShowAdjustStockModal(true)}
            className="btn-secondary"
          >
            {t('stockMovements.adjustStockButton')}
          </button>
          <button
            onClick={() => setShowAddStockModal(true)}
            className="btn-primary"
          >
            {t('stockMovements.addStockButton')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <StatsCard
          label={t('stockMovements.totalMovements')}
          value={(movements || []).length}
          color="gray"
        />
        <StatsCard label={t('stockMovements.stockIn')} value={`+${totalIn}`} color="green" />
        <StatsCard label={t('stockMovements.stockOut')} value={`-${totalOut}`} color="red" />
        <StatsCard label={t('stockMovements.lowStock')} value={lowStockCount} color="orange" />
        <StatsCard label={t('stockMovements.outOfStock')} value={outOfStockCount} color="red" />
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => switchTab('movements')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'movements'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('stockMovements.movementsTab', { count: filteredMovements.length })}
          </button>
          <button
            onClick={() => switchTab('levels')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'levels'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('stockMovements.stockLevelsTab', { count: filteredLevels.length })}
          </button>
          <button
            onClick={() => switchTab('alerts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm relative ${
              activeTab === 'alerts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('stockMovements.lowStockAlertsTab')}
            {(alerts || []).length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {alerts.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="card mb-6 py-3">
        {searchTerm && (
          <p className="text-sm text-gray-600 mb-2">
            {t('stockMovements.searching', { term: searchTerm })}
          </p>
        )}
        {(activeTab === 'movements' || activeTab === 'alerts') && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
              {t('stockMovements.supplier')}
            </span>
            <button
              onClick={() => setFilterSupplier('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap shrink-0 transition-colors ${
                filterSupplier === ''
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
              }`}
            >
              {t('common.all')}
            </button>
            {(activeTab === 'movements'
              ? supplierOptions
              : alertSupplierOptions
            ).map((supplier) => (
              <button
                key={supplier}
                onClick={() =>
                  setFilterSupplier(filterSupplier === supplier ? '' : supplier)
                }
                className={`px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap shrink-0 transition-colors ${
                  filterSupplier === supplier
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                }`}
              >
                {supplier}
              </button>
            ))}
          </div>
        )}
        {activeTab === 'levels' && (
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field"
            >
              <option value="">{t('stockMovements.allStatus')}</option>
              <option value="in_stock">{t('stockMovements.inStock')}</option>
              <option value="low_stock">{t('stockMovements.lowStock')}</option>
              <option value="out_of_stock">{t('stockMovements.outOfStock')}</option>
            </select>
            {filterStatus && (
              <button
                onClick={() => setFilterStatus('')}
                className="btn-secondary"
              >
                {t('stockMovements.clear')}
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Movements Tab */}
          {activeTab === 'movements' && (
            <div className="card overflow-hidden">
              {filteredMovements.length === 0 ? (
                <EmptyState
                  icon="📊"
                  title={t('stockMovements.noMovementsTitle')}
                  message={t('stockMovements.noMovementsMessage')}
                  hasItems={(movements || []).length > 0}
                />
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.part')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.type')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.qty')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.currentStock')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.daysUntilEmpty')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.reference')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.supplierHeader')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('stockMovements.user')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('common.date')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-normal text-gray-500 uppercase">
                        {t('common.notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMovements.map((movement) => (
                      <tr key={movement.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <button
                            onClick={() => openPart(movement.part_id)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 text-left"
                          >
                            {movement.part_name}
                          </button>
                          {movement.part_color && (
                            <p className="text-xs text-gray-500">
                              {movement.part_color}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getMovementTypeColor(movement.movement_type)}`}
                          >
                            {movement.movement_type?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`text-sm font-semibold ${
                              ['in', 'order_delivery'].includes(
                                movement.movement_type,
                              )
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {['in', 'order_delivery'].includes(
                              movement.movement_type,
                            )
                              ? '+'
                              : '-'}
                            {movement.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500 text-right">
                          {movement.current_stock || '-'}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500 text-right">
                          {movement.days_until_empty || '-'}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500">
                          {movement.reference_type && (
                            <span>
                              {movement.reference_type}
                              {movement.reference_id &&
                                ` #${movement.reference_id}`}
                            </span>
                          )}
                          {!movement.reference_type && '-'}
                        </td>

                        <td className="px-4 py-4 text-xs text-gray-500">
                          {movement.supplier || '-'}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500">
                          {movement.user_name || '-'}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500">
                          {formatDate(movement.created_at)}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500 max-w-xs truncate">
                          {movement.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Levels Tab */}
          {activeTab === 'levels' && (
            <div className="card overflow-hidden">
              {filteredLevels.length === 0 ? (
                <EmptyState
                  icon="📦"
                  title={t('stockMovements.noStockLevelsTitle')}
                  message={t('stockMovements.noStockLevelsMessage')}
                  hasItems={(levels || []).length > 0}
                />
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.part')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.orderNumber')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.quantity')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.minLevel')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('common.status')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLevels.map((level) => {
                      const status = getStockStatusKey(level);
                      return (
                        <tr key={level.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <button
                              onClick={() => openPart(level.id)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              {level.part_name}
                            </button>
                            {level.color_name && (
                              <p className="text-xs text-gray-500">
                                {level.color_name}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {renderOpenOrderCell(level)}
                          </td>
                          <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                            {level.total_quantity || 0}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {level.min_stock_level || 5}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getStockStatusColor(status)}`}
                            >
                              {stockStatusLabel(status)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right space-x-2">
                            <button
                              onClick={() => {
                                setAddStockData({
                                  part_id: level.id,
                                  quantity: '',
                                  notes: t('stockMovements.restockingNotes', { name: level.part_name }),
                                });
                                setShowAddStockModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 text-sm"
                            >
                              {t('stockMovements.add')}
                            </button>
                            <button
                              onClick={() => {
                                setAdjustStockData({
                                  part_id: level.id,
                                  quantity: level.total_quantity || 0,
                                  notes: '',
                                });
                                setShowAdjustStockModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-sm"
                            >
                              {t('stockMovements.adjust')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="card overflow-hidden">
              {filteredAlerts.length === 0 ? (
                <EmptyState
                  icon="✅"
                  title={t('stockMovements.noAlertsTitle')}
                  message={t('stockMovements.noAlertsMessage')}
                  hasItems={(alerts || []).length > 0}
                />
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.part')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.current')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.minLevel')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('common.status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.orderNr')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.daysUntilEmptyHeader')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('stockMovements.supplierHeader')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAlerts.map((alert) => {
                      return (
                        <tr
                          key={alert.id}
                          className="hover:bg-gray-50 bg-red-50"
                        >
                          <td className="px-4 py-4">
                            <p className="text-sm font-medium text-gray-900">
                              {alert.name}
                            </p>
                            {alert.color && (
                              <p className="text-xs text-gray-500">
                                {alert.color}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm font-semibold text-red-600">
                            {alert.quantity || 0}
                          </td>

                          <td className="px-4 py-4 text-sm text-gray-500">
                            {alert.min_stock_level || 5}
                          </td>

                          <td className="px-4 py-4">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getStockStatusColor(getStockStatusKey(alert))}`}
                            >
                              {stockStatusLabel(getStockStatusKey(alert))}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {renderOpenOrderCell(alert)}
                          </td>
                          <td className="px-4 py-4 text-xs text-gray-500">
                            {getDaysUntilEmpty(alert.id) ?? '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {alert.supplier || t('stockMovements.notAvailable')}
                          </td>

                          <td className="px-4 py-4 text-right">
                            <button
                              onClick={() => {
                                setAddStockData({
                                  part_id: alert.id,
                                  quantity: '',
                                  notes: t('stockMovements.restockingNotes', { name: alert.name }),
                                });
                                setShowAddStockModal(true);
                              }}
                              className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
                            >
                              {t('stockMovements.addStockButton')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Add Stock Modal */}
      <Modal
        isOpen={showAddStockModal}
        onClose={() => setShowAddStockModal(false)}
        title={t('stockMovements.addStockModalTitle')}
        size="md"
      >
        <form onSubmit={handleAddStock} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('stockMovements.part')}
            </label>
            <Select
              options={partOptions}
              value={partOptions.find(
                (opt) => opt.value === addStockData.part_id,
              )}
              onChange={(selectedOption) =>
                setAddStockData({
                  ...addStockData,
                  part_id: selectedOption ? selectedOption.value : '',
                })
              }
              placeholder={t('stockMovements.searchPartPlaceholder')}
              isClearable
              classNamePrefix="react-select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('stockMovements.quantityToAdd')}
            </label>
            <input
              type="number"
              value={addStockData.quantity}
              onChange={(e) =>
                setAddStockData({ ...addStockData, quantity: e.target.value })
              }
              className="input-field"
              placeholder={t('stockMovements.enterQuantity')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.notes')}
            </label>
            <textarea
              value={addStockData.notes}
              onChange={(e) =>
                setAddStockData({ ...addStockData, notes: e.target.value })
              }
              className="input-field"
              rows="2"
              placeholder={t('stockMovements.restockNotesPlaceholder')}
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowAddStockModal(false)}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-success">
              {t('stockMovements.addStockButton')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Adjust Stock Modal */}
      <Modal
        isOpen={showAdjustStockModal}
        onClose={() => setShowAdjustStockModal(false)}
        title={t('stockMovements.adjustStockModalTitle')}
        size="md"
      >
        <form onSubmit={handleAdjustStock} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('stockMovements.part')}
            </label>
            <Select
              classNamePrefix="react-select"
              options={partOptions}
              value={
                partOptions.find(
                  (opt) => opt.value === parseInt(adjustStockData.part_id),
                ) || null
              }
              onChange={(selectedOption) => {
                const part = selectedOption
                  ? selectedOption.originalPart
                  : null;
                setAdjustStockData({
                  ...adjustStockData,
                  part_id: selectedOption
                    ? selectedOption.value.toString()
                    : '',
                  quantity: part ? part.quantity || 0 : '',
                });
              }}
              placeholder={t('stockMovements.selectOrSearchPart')}
              isClearable
              required
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: '0.375rem',
                  borderColor: '#D1D5DB',
                  minHeight: '42px',
                }),
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('stockMovements.newQuantity')}
            </label>
            <input
              type="number"
              value={adjustStockData.quantity}
              onChange={(e) =>
                setAdjustStockData({
                  ...adjustStockData,
                  quantity: e.target.value,
                })
              }
              className="input-field"
              placeholder={t('stockMovements.enterNewQuantity')}
              min="0"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('stockMovements.reason')}
            </label>
            <textarea
              value={adjustStockData.notes}
              onChange={(e) =>
                setAdjustStockData({
                  ...adjustStockData,
                  notes: e.target.value,
                })
              }
              className="input-field"
              rows="2"
              placeholder={t('stockMovements.reasonPlaceholder')}
              required
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowAdjustStockModal(false)}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {t('stockMovements.adjustStockButton')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StockMovements;
